import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Pencil, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

interface Company {
  id?: number;
  name: string;
  code: string;
  instanceId: string;
  status: string;
  description: string;
  organisationId: string;
  expenseSubscriptionId: string;
  expenseUserEmail: string;
  expenseAccessToken: string;
  approvedExpensesEnabled: boolean;
  creditCardExpensesEnabled: boolean;
  financialsInstanceId: string;
}

const emptyCompany: Company = {
  name: '',
  code: '',
  instanceId: '',
  status: 'active',
  description: '',
  organisationId: '',
  expenseSubscriptionId: '',
  expenseUserEmail: '',
  expenseAccessToken: '',
  approvedExpensesEnabled: true,
  creditCardExpensesEnabled: false,
  financialsInstanceId: '',
};

export function SettingsIntegrations() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<Company>(emptyCompany);
  const [isSaving, setIsSaving] = useState(false);
  const [syncingCompanyId, setSyncingCompanyId] = useState<number | null>(null);
  const [syncingClientsCompanyId, setSyncingClientsCompanyId] = useState<number | null>(null);

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiFetch<Company[]>(`${getApiBaseUrl()}/companies`);
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleCreate = () => {
    setEditingCompany(null);
    setFormData(emptyCompany);
    setIsDialogOpen(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData(company);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this company?')) return;

    try {
      await apiFetch<void>(`${getApiBaseUrl()}/companies/${id}`, {
        method: 'DELETE',
      });
      toast.success('Company deleted successfully');
      loadCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const url = editingCompany
        ? `${getApiBaseUrl()}/companies/${editingCompany.id}`
        : `${getApiBaseUrl()}/companies`;
      
      const method = editingCompany ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        organisationId: formData.organisationId || null,
        instanceId: formData.instanceId || null,
        description: formData.description || null,
        expenseSubscriptionId: formData.expenseSubscriptionId || null,
        expenseUserEmail: formData.expenseUserEmail || null,
        expenseAccessToken: formData.expenseAccessToken || null,
        approvedExpensesEnabled: formData.approvedExpensesEnabled,
        creditCardExpensesEnabled: formData.creditCardExpensesEnabled,
        financialsInstanceId: formData.financialsInstanceId || null,
      };
      
      await apiFetch<Company>(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      toast.success(editingCompany ? 'Company updated successfully' : 'Company created successfully');
      setIsDialogOpen(false);
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Failed to save company');
    } finally {
      setIsSaving(false);
    }
  };

  const pollSyncStatus = async (companyId: number): Promise<void> => {
    const maxAttempts = 120;
    const pollInterval = 3000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      try {
        const status = await apiFetch<{
          status: string;
          message?: string;
          projectsSynced?: number;
          costCentresSynced?: number;
          error?: string;
        }>(`${getApiBaseUrl()}/companies/${companyId}/sync/finops/status`);

        if (status.status === 'completed') {
          toast.success(status.message || 'FinOps sync completed', {
            description: `Projects: ${status.projectsSynced ?? 0}, Cost Centres: ${status.costCentresSynced ?? 0}`,
          });
          return;
        }

        if (status.status === 'failed') {
          toast.error('FinOps sync failed', {
            description: status.error || status.message || 'Unknown error',
          });
          return;
        }
      } catch {
        console.error('Error polling sync status');
      }
    }

    toast.warning('Sync is still running in the background. Check back later.');
  };

  const handleSyncFinOps = async (companyId: number) => {
    try {
      setSyncingCompanyId(companyId);
      await apiFetch<{ message?: string }>(
        `${getApiBaseUrl()}/companies/${companyId}/sync/finops`,
        { method: 'POST' }
      );

      toast.info('Sync started in the background...', {
        description: 'Fetching projects and cost centres from FinOps. This may take a moment.',
      });

      await pollSyncStatus(companyId);
    } catch (error) {
      console.error('Error syncing FinOps:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start FinOps sync');
    } finally {
      setSyncingCompanyId(null);
    }
  };

  const handleSyncClients = async (companyId: number) => {
    try {
      setSyncingClientsCompanyId(companyId);
      const result = await apiFetch<{ message?: string; clientsCreated?: number; clientsUpdated?: number }>(
        `${getApiBaseUrl()}/companies/${companyId}/sync/clients`,
        { method: 'POST' }
      );

      toast.success(result.message || 'Clients synced successfully', {
        description: `Created: ${result.clientsCreated}, Updated: ${result.clientsUpdated}`,
      });
    } catch (error) {
      console.error('Error syncing clients:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to sync clients');
    } finally {
      setSyncingClientsCompanyId(null);
    }
  };

  const handleInputChange = (field: keyof Company, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Integrations</h2>
          <p className="text-muted-foreground mt-1">
            Manage company integrations and sync settings
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Company
        </Button>
      </div>

      <div className="grid gap-4">
        {companies.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">No companies configured yet</p>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Company
              </Button>
            </CardContent>
          </Card>
        ) : (
          companies.map((company) => (
            <Card key={company.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle>{company.name}</CardTitle>
                    <CardDescription className="mt-1">
                      Code: {company.code} | Instance: {company.instanceId || 'N/A'} | Status: {company.status}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(company)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => company.id && handleDelete(company.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.description && (
                  <p className="text-sm text-muted-foreground">{company.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Organisation ID:</span>
                    <p className="text-muted-foreground">{company.organisationId || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Expense Subscription ID:</span>
                    <p className="text-muted-foreground">{company.expenseSubscriptionId || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Expense User Email:</span>
                    <p className="text-muted-foreground">{company.expenseUserEmail || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Financials Instance ID:</span>
                    <p className="text-muted-foreground">{company.financialsInstanceId || 'Not set'}</p>
                  </div>
                </div>

                <div className="pt-4 border-t flex flex-wrap gap-2">
                  <Button
                    onClick={() => company.id && handleSyncFinOps(company.id)}
                    disabled={syncingCompanyId === company.id}
                    className="w-full sm:w-auto"
                  >
                    {syncingCompanyId === company.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sync FinOps Projects and Cost Centres
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => company.id && handleSyncClients(company.id)}
                    disabled={syncingClientsCompanyId === company.id}
                    className="w-full sm:w-auto"
                  >
                    {syncingClientsCompanyId === company.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Syncing Clients...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sync Clients
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
            <DialogDescription>
              {editingCompany ? 'Update company details and integration settings' : 'Create a new company integration'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  placeholder="Company code"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instanceId">Instance ID</Label>
                <Input
                  id="instanceId"
                  value={formData.instanceId}
                  onChange={(e) => handleInputChange('instanceId', e.target.value)}
                  placeholder="Instance identifier"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleInputChange('status', value)}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Company description"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organisationId">Organisation ID</Label>
              <Input
                id="organisationId"
                value={formData.organisationId}
                onChange={(e) => handleInputChange('organisationId', e.target.value)}
                placeholder="Organisation GUID"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="financialsInstanceId">Financials Instance ID</Label>
              <Input
                id="financialsInstanceId"
                value={formData.financialsInstanceId}
                onChange={(e) => handleInputChange('financialsInstanceId', e.target.value)}
                placeholder="Financials instance identifier"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expenseSubscriptionId">Expense Subscription ID</Label>
              <Input
                id="expenseSubscriptionId"
                value={formData.expenseSubscriptionId}
                onChange={(e) => handleInputChange('expenseSubscriptionId', e.target.value)}
                placeholder="Expense subscription identifier"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expenseUserEmail">Expense User Email</Label>
              <Input
                id="expenseUserEmail"
                type="email"
                value={formData.expenseUserEmail}
                onChange={(e) => handleInputChange('expenseUserEmail', e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expenseAccessToken">Expense Access Token</Label>
              <Textarea
                id="expenseAccessToken"
                value={formData.expenseAccessToken}
                onChange={(e) => handleInputChange('expenseAccessToken', e.target.value)}
                placeholder="Access token for expense integration"
                rows={3}
              />
            </div>

            <div className="space-y-3 pt-2">
              <Label className="text-base">Expense Integration Features</Label>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="approvedExpensesEnabled"
                    checked={formData.approvedExpensesEnabled}
                    onCheckedChange={(checked) =>
                      setFormData(prev => ({ ...prev, approvedExpensesEnabled: checked === true }))
                    }
                  />
                  <label htmlFor="approvedExpensesEnabled" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Approved Expenses
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="creditCardExpensesEnabled"
                    checked={formData.creditCardExpensesEnabled}
                    onCheckedChange={(checked) =>
                      setFormData(prev => ({ ...prev, creditCardExpensesEnabled: checked === true }))
                    }
                  />
                  <label htmlFor="creditCardExpensesEnabled" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Credit Card Expenses
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !formData.name || !formData.code}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
